import { useNavigate, useParams } from "react-router-dom";
import { VentureForm, useVenture, useVentureUpdate, useVenturePublish } from "@project-mgmt/feature-ventures";
import { Container, Page, Button } from "@project-mgmt/ui";
import { routes } from "../utils/routes";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { notify } from "../providers/ToastProvider";
import type { UpdateVenture } from "@project-mgmt/validation";

export function EditVenturePage() {
  const navigate = useNavigate();
  const { workspaceId, ventureId } = useParams<{ workspaceId: string; ventureId: string }>();

  // Fetch workspace and venture data
  const { venture, isLoading, error } = useVenture(ventureId ?? "", workspaceId ?? "");

  // Venture update and publish actions
  const { updateVenture } = useVentureUpdate(ventureId ?? "", workspaceId ?? "", notify);
  const { publishVenture } = useVenturePublish(ventureId ?? "", workspaceId ?? "", notify);

  // Validate required params
  if (!workspaceId || !ventureId) {
    return (
      <Container>
        <Page>
          <div className="p-6 text-center">
            <p className="text-red-600">Invalid workspace or venture ID</p>
            <Button onClick={() => navigate(routes.workspaces())} className="mt-4">
              Back to Workspaces
            </Button>
          </div>
        </Page>
      </Container>
    );
  }

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error state
  if (error || !venture) {
    return (
      <Container>
        <Page>
          <div className="p-6 text-center">
            <p className="text-red-600">{error || "Venture not found"}</p>
            <Button onClick={() => navigate(routes.ventures(workspaceId))} className="mt-4">
              Back to Ventures
            </Button>
          </div>
        </Page>
      </Container>
    );
  }

  // Check if venture can be edited
  if (venture.status !== "draft" && venture.status !== "live") {
    return (
      <Container>
        <Page>
          <div className="p-6 text-center">
            <p className="text-red-600">This venture cannot be edited in its current status</p>
            <Button onClick={() => navigate(routes.ventureDetail(workspaceId, ventureId))} className="mt-4">
              Back to Venture
            </Button>
          </div>
        </Page>
      </Container>
    );
  }

  // Handle draft save (for draft ventures)
  const handleSaveDraft = async (data: UpdateVenture | any) => {
    const result = await updateVenture(data as UpdateVenture);
    if (result.success && result.data) {
      notify.success("Venture draft saved");
      return result.data;
    }
    throw new Error("Failed to save draft");
  };

  // Handle publish (changes status to 'live')
  const handlePublish = async (ventureIdToPublish: string) => {
    const result = await publishVenture();
    if (result.success) {
      navigate(routes.ventureDetail(workspaceId, ventureIdToPublish));
    }
  };

  // Handle successful venture update (saves changes)
  const handleSubmit = async (data: UpdateVenture | any) => {
    const result = await updateVenture(data as UpdateVenture);
    if (result.success) {
      navigate(routes.ventureDetail(workspaceId, ventureId));
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(routes.ventureDetail(workspaceId, ventureId));
  };

  return (
    <Container>
      <Page>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-textPrimary text-2xl font-bold">Edit Venture</h1>
          <p className="text-textSecondary mt-1 text-sm">Update your venture proposal details</p>
        </div>

        {/* Venture Form */}
        <VentureForm
          workspaceId={workspaceId}
          venture={venture}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onCancel={handleCancel}
        />
      </Page>
    </Container>
  );
}
